/**
 * lib/data/pro-member-videos.ts
 * Updated: toSchemaUploadDate applied at JSON-LD emission boundary (cycle 2026-06-16).
 * Updated: API-sourced metadata via video-metadata.generated.json (cycle 2026-06-16).
 *
 * SINGLE SOURCE OF TRUTH for all public BunnyNet videos on the Pro Member sales page.
 *
 * ─── HOW THIS WORKS ──────────────────────────────────────────────────────────────
 *
 * This module declares every public BunnyNet video shown on /pro-member.
 * Three consumers import from it:
 *
 *   1. app/pro-member/_client/PMInteractiveClient.tsx  — carousel VIDEO_CARDS array
 *   2. app/pro-member/page.tsx                         — VideoObject JSON-LD @graph entries
 *   3. app/video-sitemap.xml/route.ts                  — <video:video> sitemap entries
 *
 * Changing a carousel card (title, description, duration) in this file flows into
 * ALL THREE automatically — no second list to update.
 *
 * ─── METADATA AUTO-SYNC (DO NOT HAND-ENTER DURATION / UPLOAD DATE) ───────────────
 *
 * Duration (durationSec, durationISO) and uploadDate for every video are
 * auto-synced from the BunnyNet API by scripts/sync-video-metadata.mjs.
 * That script runs automatically in the predeploy chain (before every deploy).
 * Results are committed to lib/data/video-metadata.generated.json.
 *
 * The buildVideoObjectJsonLd() function reads from that cache at runtime.
 * The API value WINS over any declared value in this file.
 * Hand-entered durationSec / durationISO / uploadDate values in this file
 * are NON-AUTHORITATIVE — they serve only as fallbacks for a brand-new video
 * that has not yet been through a predeploy cycle.
 *
 * TO ADD A NEW VIDEO: declare its videoId + editorial fields below, then run
 * pnpm deploy (or node scripts/sync-video-metadata.mjs manually) — duration
 * and upload date are fetched automatically. Do NOT hand-type them.
 *
 * ─── GATED-CONTENT SAFETY GUARDRAIL (NON-NEGOTIABLE) ────────────────────────────
 *
 * BunnyNet library 243915 also serves gated course videos (behind login on
 * app.pianofantasy.com). This module NEVER enumerates the whole library.
 * Only videos explicitly declared here are included in the sitemap and schema.
 * Adding a gated video here would violate this rule — do NOT add any video
 * that is not embedded on a public pianofantasy.com page.
 * The sync script inherits this guarantee: it only fetches metadata for IDs
 * explicitly listed here — it never enumerates the library.
 *
 * ─── ADDING A NEW CAROUSEL VIDEO ─────────────────────────────────────────────────
 *
 * 1. Add a VideoCard entry to PRO_MEMBER_CAROUSEL_VIDEOS below.
 *    - id:          unique string, e.g. 'vc-10'
 *    - videoId:     BunnyNet UUID from library 243915
 *    - type:        'FRAGMENT' | 'ANCHOR' | 'LIVE' | 'COMMUNITY'
 *    - chip:        short label shown on the card chip
 *    - title:       exact string shown on card and in Google schema
 *    - sub:         subtitle / short description (shown on card)
 *    - description: longer description for schema/sitemap (may match sub, or be fuller)
 *    - duration:    human-readable for card display, e.g. '3:22' (editorial label only)
 *    - durationISO: LEAVE AS PLACEHOLDER — auto-synced from BunnyNet API
 *    - durationSec: LEAVE AS PLACEHOLDER — auto-synced from BunnyNet API
 *    - uploadDate:  LEAVE AS PLACEHOLDER — auto-synced from BunnyNet API
 *    - posterSlug:  folder name under public/images/pro-member/[posterSlug]/poster.webp
 *
 * 2. Add the poster image: public/images/pro-member/[posterSlug]/poster.webp
 *
 * 3. That's it. The carousel, JSON-LD, and video-sitemap all update automatically.
 *    Duration + uploadDate will be fetched from BunnyNet on the next predeploy.
 *
 * ─── ADDING A FUTURE SALES PAGE ──────────────────────────────────────────────────
 *
 * Create a new file, e.g. lib/data/super-fingers-videos.ts, following this same
 * pattern. Import it in that page's page.tsx (for JSON-LD) and in
 * app/video-sitemap.xml/route.ts (add a new push block). The carousel component
 * for that page imports the VIDEO_CARDS from its own data file.
 *
 * Cycle 2026-06-15 (Vela) — extracted from duplicate hardcoded arrays in page.tsx
 * and video-sitemap.xml/route.ts.
 * Cycle 2026-06-16 (Vela) — API metadata sync via video-metadata.generated.json.
 */

import { toSchemaUploadDate, secondsToIso } from '@/lib/schema-date';

// ─── Video metadata cache type ────────────────────────────────────────────────
// Populated by scripts/sync-video-metadata.mjs (runs in predeploy automatically).
// The API value WINS over any declared value in this file.
// The cache is loaded server-side by the consuming page.tsx / route.ts —
// NOT here, because this module is also imported by Client Components and
// cannot use Node.js `fs`/`path` APIs.

export interface CachedVideoMeta {
  durationSec: number | null;
  durationISO: string | null;
  uploadDate: string | null;
}

export type VideoMetadataCache = Record<string, CachedVideoMeta>;

export const BUNNY_LIB_ID = '243915';
const BUNNY_PLAYER_BASE = 'https://player.mediadelivery.net/embed';
const THUMB_PROXY_BASE = 'https://pianofantasy.com/api/bunny-thumb';

/** Build a BunnyNet embed URL with autoplay. */
export function bunnyEmbedUrl(videoId: string): string {
  return `${BUNNY_PLAYER_BASE}/${BUNNY_LIB_ID}/${videoId}?autoplay=true&playsinline=true`;
}

/** Build the thumbnail proxy URL (Googlebot-crawlable). */
export function bunnyThumbProxyUrl(videoId: string): string {
  return `${THUMB_PROXY_BASE}/${videoId}`;
}

/** Build the plain embed URL without autoplay (for sitemaps + schema). */
export function bunnySchemaUrl(videoId: string): string {
  return `${BUNNY_PLAYER_BASE}/${BUNNY_LIB_ID}/${videoId}`;
}

// ─── Type: VideoCardType ──────────────────────────────────────────────────────
// Matches the union used in PMInteractiveClient for card rendering.
export type VideoCardType = 'FRAGMENT' | 'ANCHOR' | 'LIVE' | 'COMMUNITY';

// ─── Type: ProMemberVideoCard ─────────────────────────────────────────────────
// Single declaration drives: carousel card, VideoObject JSON-LD, sitemap entry.
export interface ProMemberVideoCard {
  /** Stable ID for React key and JSON-LD @id fragment, e.g. 'vc-1'. */
  id: string;
  /** BunnyNet video UUID in library 243915. */
  videoId: string;
  /** Carousel card type — controls the chip label colour and type marker. */
  type: VideoCardType;
  /** Short label shown on the chip above the card title. */
  chip: string;
  /** Exact title — shown on card, in JSON-LD name, in sitemap title. */
  title: string;
  /** Short subtitle line shown on the carousel card body (optional). */
  sub?: string;
  /**
   * Full description for JSON-LD VideoObject.description and sitemap
   * <video:description>. May be richer than `sub`.
   */
  description: string;
  /** Human-readable duration label for the card, e.g. '2:08'. */
  duration: string;
  /** ISO 8601 duration for schema.org VideoObject, e.g. 'PT2M8S'. */
  durationISO: string;
  /** Duration in whole seconds for the video-sitemap <video:duration> tag. */
  durationSec: number;
  /** ISO 8601 date the video was uploaded/published, e.g. '2026-05-22'. */
  uploadDate: string;
  /**
   * Poster image slug. Poster lives at:
   *   public/images/pro-member/[posterSlug]/poster.webp
   */
  posterSlug: string;
}

// ─── Hero video ───────────────────────────────────────────────────────────────
// The §1 hero video is separate from the carousel — it uses a different poster
// path convention (PMHeroClient has its own poster constant) and a different
// autoplay URL format (no queue, added to the hero iframe directly).
// The videoId here is the only value PMHeroClient needs from this module.

export const PRO_MEMBER_HERO_VIDEO = {
  id: 'hero',
  videoId: '5e35dceb-64d8-478d-a7f2-2c611cf88930',
  title: 'What is Piano Fantasy Pro Member?',
  description:
    'An introduction to Piano Fantasy Pro Member — structured piano instruction with mechanism-led courses, monthly live lessons, daily community, and a full resource library.',
  durationISO: 'PT1M42S',
  durationSec: 102,
  uploadDate: '2026-05-19',
} as const;

// ─── Carousel videos ──────────────────────────────────────────────────────────
// 9 cards. Titles/descriptions: Hazel + Clara Round 2 sign-off (2026-05-22).
// Bunny UUIDs: Lars's canonical mapping.
// To add a new card: append to this array and add the poster image (see header).

export const PRO_MEMBER_CAROUSEL_VIDEOS: ProMemberVideoCard[] = [
  {
    id: 'vc-1',
    videoId: '26b9fa5c-33df-4a5f-b123-74ad10b3a7f6',
    type: 'FRAGMENT',
    chip: 'Articulation',
    title: '"My staccato sounds heavy — it never gets really short"',
    sub: 'Pizzicato from the fingers — not from the arm.',
    description:
      'Pizzicato from the fingers — not from the arm. A lesson fragment on articulation technique from Piano Fantasy Pro Member.',
    duration: '2:08',
    durationISO: 'PT2M8S',
    durationSec: 128,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-pizzicato',
  },
  {
    id: 'vc-2',
    videoId: '116b4634-403c-4ade-9384-b1aab0e0d4a0',
    type: 'LIVE',
    chip: 'Live coaching',
    title: 'LIVE coaching — "My fourth finger disappears when I speed it up"',
    sub: 'Concentrated fingers, not spaghetti fingers — finger independence and the anchor finger.',
    description:
      'Concentrated fingers, not spaghetti fingers — finger independence and the anchor finger. Live coaching excerpt from Piano Fantasy Pro Member.',
    duration: '7:48',
    durationISO: 'PT7M48S',
    durationSec: 468,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-czerny-live',
  },
  {
    id: 'vc-3',
    videoId: 'ebb67f78-a5be-463f-bdca-cd626c889598',
    type: 'ANCHOR',
    chip: 'Chromatic scale',
    title: 'The chromatic scale as a full-hand coordination exercise',
    sub: 'Full module from the Super Fingers course.',
    description:
      'Full module from the Super Fingers course. Covers the chromatic scale as a hand coordination exercise across all fingers.',
    duration: '15:58',
    durationISO: 'PT15M58S',
    durationSec: 958,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-chromatic-full',
  },
  {
    id: 'vc-4',
    videoId: 'd781ebd5-fe27-427d-9cc8-0658796b79e8',
    type: 'FRAGMENT',
    chip: 'Chord preparation',
    title: '"I always land the chord wrong — the top voice disappears"',
    sub: 'The preparation window: arm weight before finger contact.',
    description:
      'The preparation window: arm weight before finger contact. A lesson fragment on chord preparation from Piano Fantasy Pro Member.',
    duration: '3:22',
    durationISO: 'PT3M22S',
    // BunnyNet API value from initial sitemap build (2026-06-15): 153s
    durationSec: 153,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-chord-prep',
  },
  {
    id: 'vc-5',
    videoId: '94246302-86f1-42d8-b01c-ac74f7c41d0f',
    type: 'FRAGMENT',
    chip: 'Scales',
    title: '"My scales sound uneven — some notes louder, some softer"',
    sub: 'C major as a listening exercise — matching the weight across each finger.',
    description:
      'C major as a listening exercise — matching the weight across each finger. From the Piano Fantasy Pro Member course.',
    duration: '1:38',
    durationISO: 'PT1M38S',
    durationSec: 98,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-scales-c',
  },
  {
    id: 'vc-6',
    videoId: 'fa273f74-e6a2-4f77-ba19-a6cccc16cefa',
    type: 'ANCHOR',
    chip: 'Etude work',
    title: 'How to work an etude without turning it into mindless repetition',
    sub: 'Burgmüller Op. 100 No. 2 — isolating the hand position shift.',
    description:
      'Burgmüller Op. 100 No. 2 — isolating the hand position shift. The three phases of deliberate practice from Piano Fantasy Pro Member.',
    duration: '5:31',
    durationISO: 'PT5M31S',
    durationSec: 331,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-burgmuller',
  },
  {
    id: 'vc-7',
    videoId: '19470b5f-00d9-4799-ae06-2371529d82a3',
    type: 'LIVE',
    chip: 'Live coaching',
    title: 'LIVE coaching — "The left hand pedal point keeps crashing into the melody"',
    sub: 'Bach Prelude in C: separating hand weight and finger independence.',
    description:
      'Bach Prelude in C: separating hand weight and finger independence. Live coaching excerpt from Piano Fantasy Pro Member.',
    duration: '3:25',
    durationISO: 'PT3M25S',
    durationSec: 205,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-bach-pedal-live',
  },
  {
    id: 'vc-8',
    videoId: 'e84c3ad5-3972-4148-be7b-797a26396fef',
    type: 'FRAGMENT',
    chip: 'Hand independence',
    title: '"I can play each hand alone — but together it falls apart"',
    sub: 'The coordination sequence: slow subdivision before full tempo.',
    description:
      'The coordination sequence: slow subdivision before full tempo. A lesson on hand independence from Piano Fantasy Pro Member.',
    duration: '2:01',
    durationISO: 'PT2M1S',
    durationSec: 121,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-hand-independence',
  },
  {
    id: 'vc-9',
    videoId: '8750ee54-9045-47d2-a6bc-356b86a80582',
    type: 'COMMUNITY',
    chip: 'Community',
    title: 'Community feedback thread: a member shares a recording of Chopin Op. 28 No. 6',
    sub: 'What the group hears — and what Lars adds.',
    description:
      'What the group hears — and what Lars adds. A peek inside the Piano Fantasy Pro Member community feedback session.',
    duration: '1:46',
    durationISO: 'PT1M46S',
    durationSec: 106,
    uploadDate: '2026-05-22',
    posterSlug: 'carousel-community',
  },
];

// ─── Derived: sitemap-ready VideoEntry array ──────────────────────────────────
// Consumed by app/video-sitemap.xml/route.ts.
// Matches the VideoEntry interface in that file exactly.

/**
 * Per-card field overrides, keyed by card id, merged over the declarations in
 * this module by a page that passes them (currently /pro-member and
 * /pro-member-v12 — see lib/data/pro-member-carousel-overrides.ts). The array
 * above is NEVER edited: /join renders it directly and must keep the old
 * community video. Shape-compatible with PMInteractiveClient's cardOverrides
 * prop, so one object serves the carousel, the JSON-LD and the video sitemap.
 */
export type ProMemberCardOverrides = Readonly<
  Record<string, Partial<ProMemberVideoCard> & { posterSrc?: string; startSec?: number }>
>;

export interface ProMemberSitemapEntry {
  pageUrl: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  playerUrl: string;
  contentUrl?: string;
  uploadDate?: string;
  durationSeconds?: number;
}

const PM_PAGE_URL = 'https://pianofantasy.com/pro-member';

/**
 * Build the <video:video> sitemap entries for /pro-member.
 *
 * With no argument the output is byte-identical to the pre-2026-09-21 constant.
 * With overrides, each carousel card is merged before emission, so the sitemap
 * names the video the page actually plays rather than the one it replaced.
 */
export function buildProMemberSitemapEntries(
  cardOverrides?: ProMemberCardOverrides
): ProMemberSitemapEntry[] {
  return [
    // Hero video first
    {
      pageUrl: PM_PAGE_URL,
      thumbnailUrl: bunnyThumbProxyUrl(PRO_MEMBER_HERO_VIDEO.videoId),
      title: PRO_MEMBER_HERO_VIDEO.title,
      description: PRO_MEMBER_HERO_VIDEO.description,
      playerUrl: bunnySchemaUrl(PRO_MEMBER_HERO_VIDEO.videoId),
      uploadDate: PRO_MEMBER_HERO_VIDEO.uploadDate,
      durationSeconds: PRO_MEMBER_HERO_VIDEO.durationSec,
    },
    // Carousel videos
    ...PRO_MEMBER_CAROUSEL_VIDEOS.map((base) => {
      const card = { ...base, ...(cardOverrides?.[base.id] ?? {}) };
      return {
        pageUrl: PM_PAGE_URL,
        thumbnailUrl: bunnyThumbProxyUrl(card.videoId),
        title: card.title,
        description: card.description,
        playerUrl: bunnySchemaUrl(card.videoId),
        uploadDate: card.uploadDate,
        durationSeconds: card.durationSec,
      };
    }),
  ];
}

// ─── Derived: VideoObject JSON-LD array ───────────────────────────────────────
// Consumed by app/pro-member/page.tsx.
// These are the schema.org VideoObject entries in the @graph.

const PUBLISHER_ID = 'https://pianofantasy.com/about/#lars-nelissen';
const COURSE_ID = `${PM_PAGE_URL}#course`;

/**
 * Build VideoObject JSON-LD entries for the /pro-member page.
 *
 * @param pageUrl  - The canonical page URL (e.g. 'https://pianofantasy.com/pro-member')
 * @param metaCache - Optional: the video-metadata.generated.json cache, loaded server-side
 *                   by the caller (page.tsx / route.ts) using `fs`. API values WIN over
 *                   declared values in this file. Pass an empty object if unavailable.
 *
 * The cache must be loaded by the Server Component caller — this data file is also
 * imported by Client Components (PMInteractiveClient, PMHeroClient), so it cannot
 * use Node.js `fs`/`path` APIs directly.
 */
/**
 * Build a map of videoId → resolved durationSec for carousel card display.
 *
 * Priority: API cache (video-metadata.generated.json) > declared durationSec in this file.
 * Used by app/pro-member/page.tsx (Server Component) to pass resolved durations as
 * a prop to PMInteractiveClient so the client-rendered card shows the real API length.
 *
 * @param metaCache - Loaded server-side from lib/data/video-metadata.generated.json.
 *                   Pass an empty object if unavailable (falls back to declared durationSec).
 */
export function resolveCarouselDurationSecs(metaCache: VideoMetadataCache = {}): Record<string, number> {
  const result: Record<string, number> = {};
  for (const card of PRO_MEMBER_CAROUSEL_VIDEOS) {
    const cached = metaCache[card.videoId]?.durationSec;
    result[card.id] = typeof cached === 'number' && cached > 0 ? cached : card.durationSec;
  }
  return result;
}

export function buildVideoObjectJsonLd(
  pageUrl: string,
  metaCache: VideoMetadataCache = {},
  cardOverrides?: ProMemberCardOverrides
) {
  /** Return API-sourced durationISO, falling back to the declared value. */
  const resolvedDurationISO = (videoId: string, declaredISO: string): string =>
    metaCache[videoId]?.durationISO ?? declaredISO;

  /** Return API-sourced uploadDate (tz-aware), falling back to the declared value. */
  const resolvedUploadDate = (videoId: string, declaredDate: string): string =>
    metaCache[videoId]?.uploadDate ?? toSchemaUploadDate(declaredDate) ?? declaredDate;

  return [
    // Hero video — duration + uploadDate sourced from API cache when available.
    {
      '@type': 'VideoObject',
      '@id': `${pageUrl}#video-${PRO_MEMBER_HERO_VIDEO.id}`,
      name: PRO_MEMBER_HERO_VIDEO.title,
      description: PRO_MEMBER_HERO_VIDEO.description,
      thumbnailUrl: bunnyThumbProxyUrl(PRO_MEMBER_HERO_VIDEO.videoId),
      embedUrl: bunnySchemaUrl(PRO_MEMBER_HERO_VIDEO.videoId),
      uploadDate: resolvedUploadDate(PRO_MEMBER_HERO_VIDEO.videoId, PRO_MEMBER_HERO_VIDEO.uploadDate),
      duration: resolvedDurationISO(PRO_MEMBER_HERO_VIDEO.videoId, PRO_MEMBER_HERO_VIDEO.durationISO),
      publisher: { '@id': PUBLISHER_ID },
      isPartOf: { '@id': COURSE_ID },
    },
    // Carousel videos — duration + uploadDate sourced from API cache when available.
    // An override (if the caller passed any) is merged first, so the entry
    // describes the video the page actually plays. An overridden durationSec is
    // authoritative: the metadata cache is keyed by videoId and holds nothing for
    // a video that is not declared above.
    ...PRO_MEMBER_CAROUSEL_VIDEOS.map((base) => {
      const override = cardOverrides?.[base.id];
      const card = override ? { ...base, ...override } : base;
      return {
        '@type': 'VideoObject',
        '@id': `${pageUrl}#video-${card.id}`,
        name: card.title,
        description: card.description,
        thumbnailUrl: bunnyThumbProxyUrl(card.videoId),
        embedUrl: bunnySchemaUrl(card.videoId),
        uploadDate: resolvedUploadDate(card.videoId, card.uploadDate),
        duration:
          typeof override?.durationSec === 'number'
            ? secondsToIso(override.durationSec)
            : resolvedDurationISO(card.videoId, card.durationISO),
        publisher: { '@id': PUBLISHER_ID },
        isPartOf: { '@id': COURSE_ID },
      };
    }),
  ];
}
